import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])'
].join(',')

type Options = {
    // Focus an element when the form shows up (by default the first one)
    autoFocus?: boolean
    autoFocusSelector?: string
    // Wait before the initial focus (e.g. a modal fade in)
    autoFocusDelay?: number
    // Enter on a field: go to the next one ('next', for long forms) or submit the form ('submit', for short ones
    // like a modal, where Tab moves between the fields)
    enterAction?: 'next' | 'submit'
}

// Whether an element belongs to the form itself and not to a modal rendered inside it
// (a form that is inside a modal does include the elements of that modal)
const isOwnElement = (container: HTMLElement, element: HTMLElement) =>
    element.closest('.modal') === container.closest('.modal')

// Elements reachable with the keyboard, in page order. Of a radio group only the checked one counts
// (like the browser Tab does)
const getFocusables = (container: HTMLElement): HTMLElement[] => {
    const elements = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter(element => element.tabIndex >= 0 && element.offsetParent !== null && isOwnElement(container, element))
    return elements.filter(element => {
        if (!(element instanceof HTMLInputElement) || element.type !== 'radio' || element.checked) return true
        const group = elements.filter(other => other instanceof HTMLInputElement && other.type === 'radio' && other.name === element.name)
        const checked = group.find(other => (other as HTMLInputElement).checked)
        return checked ? false : group[0] === element
    })
}

const isTextInput = (element: HTMLElement) =>
    element instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(element.type)

// A focus that isn't anywhere useful: the page body or a closed modal (where the focus stays after closing one)
const isFocusLost = () => {
    const active = document.activeElement as HTMLElement | null
    return !active || active === document.body || (!!active.closest('.modal') && !active.closest('.modal.show'))
}

/**
 * Keyboard navigation of a form:
 * - Tab / Shift+Tab go to the next / previous field and wrap around inside the form (they don't go to the browser)
 * - Enter on a field goes to the next one, or submits the form with enterAction 'submit'; on a checkbox or radio
 *   it checks it; on a button it presses it (so Enter on the submit button submits, on an upload button it opens
 *   the file picker)
 */
export const useFormKeyboardNavigation = <T extends HTMLElement = HTMLDivElement>(options: Options = {}) => {
    const { autoFocus = true, autoFocusSelector, autoFocusDelay = 0, enterAction = 'next' } = options
    const containerRef = useRef<T>(null)

    const focusables = () => containerRef.current ? getFocusables(containerRef.current) : []

    const focusFirst = () => {
        const container = containerRef.current
        if (!container) return
        const preferred = autoFocusSelector ? container.querySelector<HTMLElement>(autoFocusSelector) : undefined
        const target = preferred && !preferred.hasAttribute('disabled') ? preferred : focusables()[0]
        target?.focus()
    }

    // Focus the field that follows an element (e.g. after closing a modal opened from a button)
    const focusAfter = (element?: Element | null) => {
        const list = focusables()
        const index = element ? list.indexOf(element as HTMLElement) : -1
        const next = list[index + 1] || list[0]
        next?.focus()
    }

    const focusNext = (current: HTMLElement, backwards = false) => {
        const list = focusables()
        if (list.length === 0) return
        const index = list.indexOf(current)
        const nextIndex = index === -1 ? 0 : (index + (backwards ? -1 : 1) + list.length) % list.length
        list[nextIndex].focus()
    }

    const onKeyDown = (e: React.KeyboardEvent) => {
        const target = e.target as HTMLElement
        // A modal opened from the form has its own navigation
        if (!containerRef.current || !isOwnElement(containerRef.current, target)) return

        if (e.key === 'Tab') {
            const list = focusables()
            if (list.length === 0) return
            const first = list[0]
            const last = list[list.length - 1]
            if (!e.shiftKey && target === last) {
                e.preventDefault()
                first.focus()
            } else if (e.shiftKey && target === first) {
                e.preventDefault()
                last.focus()
            }
            return
        }

        if (e.key === 'Enter' && !e.ctrlKey && !e.altKey && !e.metaKey) {
            if (target instanceof HTMLTextAreaElement || target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement) return
            // A select or autocomplete with its options open uses Enter to choose one (its own handler does it);
            // the browser default is stopped so it never submits the form from there
            if (target.dataset.optionsOpen === 'true') {
                e.preventDefault()
                return
            }
            if (target instanceof HTMLInputElement && (target.type === 'checkbox' || target.type === 'radio')) {
                e.preventDefault()
                if (!(target.type === 'radio' && target.checked)) target.click()
                return
            }
            if (isTextInput(target)) {
                e.preventDefault()
                if (enterAction === 'submit') {
                    // Same as pressing the submit button (so the form validations run)
                    (target as HTMLInputElement).form?.requestSubmit()
                } else {
                    focusNext(target)
                }
            }
        }
    }

    // When the focus is lost (e.g. after closing a modal), Tab comes back to the form instead of the browser
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Only for a visible form that isn't inside a modal (a modal keeps the focus inside itself)
            if (e.key !== 'Tab' || !containerRef.current || containerRef.current.closest('.modal')) return
            if (containerRef.current.offsetParent === null || !isFocusLost()) return
            if (document.querySelector('.modal.show')) return
            e.preventDefault()
            focusFirst()
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    useEffect(() => {
        if (!autoFocus) return
        const timeout = setTimeout(focusFirst, autoFocusDelay)
        return () => clearTimeout(timeout)
    }, [])

    return {
        containerRef,
        onKeyDown,
        focusFirst,
        focusAfter
    }
}
